import {useCallback, useEffect, useRef, useState} from 'react'
import {remotePartError} from '@dv5/api'
import type {RemotePart, RemotePartMutation} from '@dv5/api'
import {remoteDocumentPartsService} from '../services/remoteDocumentPartsService'

const sameValues = (first: RemotePart, second: RemotePart) =>
    first.partContent === second.partContent && first.partHeader === second.partHeader
    && first.partType === second.partType && first.tags === second.tags && first.layers === second.layers

export const useRemotePartEditor = (documentId: string, part: RemotePart) => {
    const [draft, setDraft] = useState<RemotePart | null>(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null)
    const draftRef = useRef<RemotePart | null>(null)
    const partRef = useRef(part)
    partRef.current = part
    const baseline = useRef(part)
    const pending = useRef<RemotePartMutation | null>(null)
    const busy = useRef(false)
    const closeAfterSave = useRef(false)
    const mounted = useRef(true)
    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    const updateDraft = useCallback((value: RemotePart | null) => {
        draftRef.current = value
        setDraft(value)
    }, [])

    const save = useCallback(async (value: RemotePart, close = true) => {
        if (close) closeAfterSave.current = true
        if (busy.current) return
        if (draftRef.current && !pending.current && sameValues(value, baseline.current)) {
            if (close) {
                updateDraft(null);
                closeAfterSave.current = false
            }
            return
        }
        if (!draftRef.current) baseline.current = partRef.current
        busy.current = true
        setSaving(true)
        setError('')
        // Retry an uncertain operation verbatim. A changed payload gets a new identity.
        if (!pending.current || JSON.stringify(pending.current.part) !== JSON.stringify(value)) {
            pending.current = {requestId: crypto.randomUUID(), part: {...value}}
        }
        try {
            const parts = await remoteDocumentPartsService.update(documentId, pending.current)
            if (!mounted.current) return
            const confirmed = parts.find(saved => saved.id === value.id)
            if (!confirmed) throw new Error('Save confirmation did not include this part. Your draft is retained.')
            pending.current = null
            baseline.current = confirmed
            setLastSavedTime(new Date())
            const current = draftRef.current
            if (!current || (closeAfterSave.current && sameValues(current, value))) {
                updateDraft(null)
                closeAfterSave.current = false
            } else {
                // Preserve typing during the request, but base the next save on the acknowledged revision.
                updateDraft({...current, revision: confirmed.revision, partIndex: confirmed.partIndex})
            }
        } catch (failure) {
            if (!mounted.current) return
            if (!draftRef.current) updateDraft({...value})
            closeAfterSave.current = false
            setError(remotePartError(failure))
        } finally {
            busy.current = false
            if (mounted.current) setSaving(false)
        }
    }, [documentId, updateDraft])

    useEffect(() => {
        if (!draft || saving || error || sameValues(draft, baseline.current)) return
        // A manual save requested during an autosave flushes subsequent edits immediately.
        const timer = setTimeout(() => {
            if (draftRef.current) void save(draftRef.current, closeAfterSave.current)
        }, closeAfterSave.current ? 0 : 2000)
        return () => clearTimeout(timer)
    }, [draft, saving, error, save])

    const cancel = () => {
        if (busy.current) return
        updateDraft(null)
        pending.current = null
        closeAfterSave.current = false
        setError('')
    }
    const changeDraft = (update: Partial<RemotePart>) => {
        if (draftRef.current) updateDraft({...draftRef.current, ...update})
    }
    const toggleEdit = () => {
        if (draftRef.current) void save(draftRef.current)
        else {
            baseline.current = partRef.current
            pending.current = null
            closeAfterSave.current = false
            setError('')
            setLastSavedTime(null)
            updateDraft({...partRef.current})
        }
    }
    const manualSave = () => {
        if (draftRef.current) void save(draftRef.current)
    }
    const retrySave = () => {
        const value = pending.current?.part || draftRef.current
        if (value) void save(value)
    }
    const saveStatus = draft && (error || saving || !sameValues(draft, baseline.current)) ? 'changed'
        : lastSavedTime ? 'saved' : 'not_changed'

    return {
        draft,
        saving,
        error,
        lastSavedTime,
        saveStatus,
        save,
        cancel,
        changeDraft,
        toggleEdit,
        manualSave,
        retrySave
    } as const
}
